/**
 * Phase PWA — Super_admin status panel for the installable web app.
 *
 * Shows current SW version, install count, last install timestamp, and
 * exposes a "Force update all clients" button. Bumping the version tag
 * causes every connected client (currently or on next page load) to
 * fetch a new SW URL → install → activate → reload.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Smartphone, RefreshCw, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

export default function PwaStatusCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data: res } = await api.get("/admin/pwa/status");
            setData(res);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onForce = async () => {
        if (!window.confirm(
            "Bump the service worker version. All connected clients will fetch the new SW on next request and silently reload. Continue?",
        )) return;
        setUpdating(true);
        try {
            const { data: res } = await api.patch("/admin/pwa/force-update");
            toast.success(`Force update issued — SW version bumped to v${res.sw_version_tag}.`);
            setData((prev) => ({ ...(prev || {}),
                sw_version_tag: res.sw_version_tag,
                force_update_at: res.force_update_at }));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setUpdating(false); }
    };

    return (
        <Card data-testid="pwa-status-card">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <Smartphone size={16} className="text-blue-900" />
                    <CardTitle className="font-display text-xl">Installable app (PWA)</CardTitle>
                </div>
                <CardDescription>
                    Service worker status, installs to date, and an emergency hatch to push
                    a new client build to every connected browser.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {loading && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                )}
                {!loading && data && (
                    <>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div className="rounded-md ring-1 ring-slate-200 bg-slate-50 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                                    Current version
                                </p>
                                <p
                                    data-testid="pwa-status-version"
                                    className="text-xl font-display font-bold text-slate-900 mt-0.5"
                                >
                                    v{data.sw_version_tag}
                                </p>
                            </div>
                            <div className="rounded-md ring-1 ring-slate-200 bg-slate-50 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                                    Installs to date
                                </p>
                                <p
                                    data-testid="pwa-status-installs"
                                    className="text-xl font-display font-bold text-slate-900 mt-0.5"
                                >
                                    {data.install_count}
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                    {data.last_install_at
                                        ? `last: ${String(data.last_install_at).slice(0, 10)}`
                                        : "no installs yet"}
                                </p>
                            </div>
                            <div className="rounded-md ring-1 ring-slate-200 bg-slate-50 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                                    Last force update
                                </p>
                                <p className="text-sm font-mono text-slate-900 mt-0.5">
                                    {data.force_update_at
                                        ? String(data.force_update_at).slice(0, 16).replace("T", " ")
                                        : "—"}
                                </p>
                            </div>
                        </div>

                        <div className="rounded-md bg-amber-50/80 ring-1 ring-amber-200 p-3 flex items-start gap-3">
                            <Sparkles size={14} className="text-amber-700 mt-0.5 shrink-0" />
                            <div className="flex-1 text-xs text-amber-900 leading-relaxed">
                                <p className="font-semibold mb-0.5">Force update all clients</p>
                                <p>
                                    Bumps the SW version tag. Every connected client (and any
                                    new browser session) installs the new build and silently
                                    reloads. Use sparingly — typically after a critical bug fix.
                                </p>
                            </div>
                            <Button
                                type="button" size="sm"
                                onClick={onForce}
                                disabled={updating}
                                data-testid="pwa-force-update-btn"
                                className="bg-amber-900 hover:bg-amber-800 text-white"
                            >
                                {updating
                                    ? <Loader2 size={12} className="animate-spin mr-1" />
                                    : <RefreshCw size={12} className="mr-1" />}
                                Force update
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
